<template>
  <b-modal v-model="visible" body-class="p-0" size="lg" hide-header hide-footer>
    <b-button
      @click="visible = false"
      class="position-absolute dark"
      variant="link"
      style="top: 0; right: 0; z-index: 99"
    >
      <b-icon icon="x"></b-icon>
    </b-button>
    <div class="row">
      <div class="col-lg-6 row align-items-end">
        <slot name="image">
          <b-img :src="imageMobile" fluid class="d-lg-none w-100"></b-img>
        </slot>
        <slot name="mobile-image">
          <b-img :src="image" fluid class="d-none d-lg-block h-100 rounded-lg"></b-img>
        </slot>
      </div>
      <div class="col-lg-6 min-h-100 d-flex">
        <div class="px-3 pb-3 row align-content-between min-h-100">
          <div class="col-12">
            <div class="mb-3 mt-3">
              <slot name="content">
                <h1>{{ heading }}</h1>
              </slot>
              <b-alert variant="success" dismissible :show="successMsg !== null" fade>{{ successMsg }}</b-alert>
              <b-alert variant="danger" style="white-space: pre-line" dismissible :show="error !== null" fade
                >{{ error }}
              </b-alert>
            </div>
            <div class="mb-3" v-if="!hideForm">
              <b-form ref="form" @submit.stop.prevent="handleSubmit">
                <b-form-group>
                  <b-form-input
                    required
                    v-model.trim="email"
                    type="email"
                    ref="email"
                    :state="emailValidation"
                    :validated="true"
                    placeholder="Work email"
                    class="form-control"
                    invalid-feedback="Email is required"
                  ></b-form-input>
                </b-form-group>

                <b-form-group v-show="step === 'password'">
                  <b-form-input
                    required
                    v-model="password"
                    @keyup="checkPasswordResults"
                    ref="password"
                    :type="passwordVisible ? 'text' : 'password'"
                    :state="passwordValidation"
                    placeholder="Password"
                    class="form-control"
                    invalid-feedback="Password is required"
                  ></b-form-input>
                </b-form-group>

                <b-form-group v-if="emailValidation && passwordValidation && type === 'signup'">
                  <b-form-input
                    required
                    v-model="passwordConfirmation"
                    @keyup="pwcFocused = true"
                    :type="passwordVisible ? 'text' : 'password'"
                    :state="passwordConfirmationValidation"
                    placeholder="Password confirmation"
                    class="form-control"
                  ></b-form-input>
                </b-form-group>

                <div
                  class="card mt-2"
                  v-if="
                    Object.keys(passwordResults).length &&
                    step === 'password' &&
                    (!passwordValidation || (type === 'signup' && !passwordConfirmationValidation))
                  "
                >
                  <div class="card-body pt-4">
                    <b-button
                      class="position-absolute unfocusable"
                      style="top: -5px; right: -8px"
                      variant="link"
                      v-if="!passwordVisible"
                      @click="passwordVisible = true"
                    >
                      <b-icon icon="eye"></b-icon>
                      Show password
                    </b-button>
                    <b-button
                      class="position-absolute unfocusable"
                      style="top: -5px; right: -8px"
                      variant="link"
                      v-else
                      @click="passwordVisible = false"
                    >
                      <b-icon icon="eye-slash"></b-icon>
                      Hide password
                    </b-button>
                    <div class="d-flex flex-row-100 my-2">
                      <div
                        class="password-bar"
                        :class="{
                          'bg-danger': passwordScore < 0.25,
                          'bg-warning': passwordScore >= 0.25 && passwordScore < 1,
                          'bg-success': passwordScore >= 1,
                        }"
                      ></div>
                      <div
                        class="password-bar"
                        :class="{
                          'bg-warning': passwordScore >= 0.5 && passwordScore < 1,
                          'bg-success': passwordScore >= 1,
                        }"
                      ></div>
                      <div
                        class="password-bar"
                        :class="{
                          'bg-warning': passwordScore >= 0.75 && passwordScore < 1,
                          'bg-success': passwordScore >= 1,
                        }"
                      ></div>
                      <div class="password-bar" :class="{ 'bg-success': passwordScore >= 1 }"></div>
                    </div>
                    <ul class="list-unstyled mb-0">
                      <li
                        v-for="(v, index) in passwordResults"
                        :key="`pwr_${index}`"
                        :class="v.valid ? 'text-success' : 'text-warning'"
                      >
                        <b-icon :icon="v.valid ? 'check2' : 'x'"></b-icon>
                        {{ v.explanation }}
                      </li>
                      <li
                        v-if="pwcFocused"
                        :class="{
                          'text-warning': password !== passwordConfirmation,
                          'text-success': password === passwordConfirmation,
                        }"
                      >
                        <b-icon :icon="password === passwordConfirmation ? 'check2' : 'x'"></b-icon>
                        {{ password === passwordConfirmation ? 'Passwords match' : "Passwords don't match" }}
                      </li>
                    </ul>
                  </div>
                </div>

                <b-form-group
                  v-if="emailValidation && passwordValidation && passwordConfirmationValidation && type === 'signup'"
                >
                  <vue-recaptcha
                    :sitekey="recaptchaKey"
                    :loadRecaptchaScript="true"
                    ref="recaptcha"
                    @verify="onRecaptchaVerified"
                  ></vue-recaptcha>
                </b-form-group>

                <div class="clearfix">
                  <div class="float-right">
                    <b-button v-if="step === 'email'" @click="handleNext" :disabled="emailSubmitted && !emailValidation"
                      >Next
                    </b-button>
                  </div>
                </div>

                <b-form-group>
                  <b-form-checkbox id="terms-of-use" v-model="acceptedTerms" name="terms-of-use">
                    I agree to Ambiki's <a :href="termsOfServicePath">Terms of Service</a> and
                    <a :href="privacyPath">Privacy Policy</a>
                  </b-form-checkbox>
                  <b-form-checkbox id="news-letter" v-model="newsLetterSubscribed" name="news-letter">
                    Subscribe to Ambiki's newsletter
                  </b-form-checkbox>
                </b-form-group>

                <b-button v-if="canSubmit" type="submit" variant="primary" :disabled="submitting"
                  >{{ type[0].toUpperCase() + type.slice(1) }}
                </b-button>
              </b-form>
            </div>
          </div>
          <b-form-group class="col-12 mt-auto">
            <ul class="list-unstyled mt-auto">
              <li v-if="type === 'signup'">
                <a href="javascript:void(0)" @click="type = 'login'">Login</a>
              </li>
              <li v-else>
                <a href="javascript:void(0)" @click="type = 'signup'">Signup</a>
              </li>
              <li v-if="type === 'login'">
                <a href="/users/password/new">Forgot your password?</a>
              </li>
              <li>
                <a href="/users/confirmation/new">Didn't receive confirmation instructions?</a>
              </li>
            </ul>
          </b-form-group>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import VueRecaptcha from 'vue-recaptcha';
import StrongPassword from '../helpers/strongPassword';
import image from '../images/registration.jpg';
import imageMobile from '../images/registration-mobile.jpg';

export default {
  components: { VueRecaptcha },

  props: {
    privacyPath: {
      type: String,
      required: true,
    },
    termsOfServicePath: {
      type: String,
      required: true,
    },
    addOn: {
      type: String,
    },
    size: {
      type: String,
      default: 'xl',
    },
    show: {
      type: Boolean,
      default: false,
    },
    recaptchaKey: {
      type: String,
      required: true,
    },
  },

  mounted() {
    const buttons = document.querySelectorAll('.register-modal-trigger');
    buttons.forEach((button) => {
      button.addEventListener('click', this.toggleModal);
    });
  },

  watch: {
    passwordConfirmationValidation(valid) {
      if (valid) this.passwordVisible = false;
    },
  },

  data() {
    return {
      visible: false,
      type: 'signup',
      email: '',
      hideForm: false,
      image,
      imageMobile,
      password: '',
      passwordConfirmation: '',
      acceptedTerms: false,
      newsLetterSubscribed: false,
      emailSubmitted: false,
      pwFocused: false,
      pwcFocused: false,
      strongPassword: new StrongPassword(),
      passwordResults: {},
      passwordScore: 0,
      passwordVisible: false,
      error: null,
      successMsg: null,
      submitting: false,
      recaptchaToken: '',
    };
  },

  computed: {
    canSubmit() {
      if (this.type === 'signup') {
        return (
          this.email.length > 0 &&
          this.password.length > 0 &&
          this.passwordConfirmation.length > 0 &&
          this.emailValidation &&
          this.passwordValidation &&
          this.passwordConfirmationValidation &&
          this.acceptedTerms
        );
      }
      return this.email.length > 0 && this.password.length > 0 && this.emailValidation && this.passwordValidation;
    },

    emailValidation() {
      if (!this.emailSubmitted) {
        return null;
      }
      const re =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(this.email) && this.email.length !== 0;
    },

    heading() {
      if (this.type === 'signup') {
        return 'Create a free account';
      }
      return 'Login to your account';
    },

    passwordValidation() {
      if (!this.pwFocused) {
        return null;
      }
      return !Object.values(this.passwordResults).find((r) => !r.valid);
    },

    passwordConfirmationValidation() {
      if (!this.pwcFocused) {
        return null;
      }
      return this.password === this.passwordConfirmation;
    },

    step() {
      if (this.emailSubmitted && this.emailValidation) {
        return 'password';
      }
      return 'email';
    },
  },

  methods: {
    onRecaptchaVerified(token) {
      this.recaptchaToken = token;
    },

    toggleModal() {
      // eslint-disable-next-line no-restricted-globals
      const button = event.target;
      if (Object.keys(button.dataset).length) {
        Object.entries(button.dataset).forEach((data) => {
          // eslint-disable-next-line no-unused-vars
          const [key, value] = data;
          if (key === 'createTrial') {
            this.createTrial = data;
          } else if (key === 'btnClicked') {
            this.btnClicked = data;
          }
        });
      }
      this.visible = true;
    },

    checkPasswordResults() {
      if (this.password.length > 1) this.pwFocused = true;
      this.strongPassword.validate(this.password).then(() => {
        this.passwordResults = this.strongPassword.results;
        this.passwordScore = this.strongPassword.score;
      });
    },

    handleNext() {
      this.emailSubmitted = true;
      if (this.emailValidation) {
        setTimeout(() => {
          this.$refs.password.focus();
        }, 100);
      } else this.$refs.email.focus();
    },

    handleSubmit() {
      this.error = null;
      this.submitting = true;
      const formData = {
        email: this.email,
        password: this.password,
      };
      let path = '/signin';
      if (this.type === 'signup') {
        formData.password_confirmation = this.passwordConfirmation;
        path = '/signup';
      }
      axios
        .post(path, {
          user: formData,
          'g-recaptcha-response': this.recaptchaToken,
          createTrial: this.createTrial,
          btnClicked: this.btnClicked,
          addOn: this.addOn,
          terms_and_privacy_agreement: this.acceptedTerms,
          newsletter_opt_in: this.newsLetterSubscribed,
        })
        .then((response) => {
          this.error = null;
          if (this.type === 'signup') {
            this.successMsg = 'Welcome! Please check your email to activate your account.';
            this.hideForm = true;
          } else {
            this.visible = false;
          }
          this.$emit('user-authenticated', { user: response.data, type: this.type });
          if (this.createTrial) {
            window.location.href = response.headers.location;
          } else {
            // TODO - need to reload the page so that
            // the new user is authenticated otherwise
            // if we refresh it deletes work they may have started
            // like for a visual schedule.
            window.reloadTurbolinks();
          }
          this.submitting = false;
        })
        .catch((err) => {
          this.successMsg = null;
          this.submitting = false;
          if (Array.isArray(err.response.data)) {
            this.error = err.response.data[0].message;
          } else if (err.response.data.errors) {
            Object.keys(err.response.data.errors).forEach((key) => {
              if (this.error === null) this.error = '';
              this.error += `${key}: ${err.response.data.errors[key][0]}\n`;
            });
          } else if (err.request) {
            this.error = "Your info wasn't received. Please try again.";
          } else {
            this.error = 'There was a problem... Please try again.';
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  font-size: 1.5em !important;
}

.modal-backdrop {
  opacity: 0.6;
}
</style>

<style lang="scss">
.modal-backdrop {
  opacity: 0.6;
}
</style>
